// @flow
import * as api from '~/core/api/install-hue-bridge';
import { getDevice } from '~/core/api/devices/devices.api';

import { put, call, takeEvery, all, select, take } from 'redux-saga/effects';
import type { Effect } from 'redux-saga';
import { delay } from 'redux-saga';
import { noop } from 'lodash/fp';

import { installPhilipsHue } from '~/core/constants/routes';

import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import { pollNodeExists } from '~/core/state/honeycomb/actions';
import { navigate } from '~/core/state/navigate';

import { formatPattern } from '~/core/utils/routes';

import {
    ADD_CHILDREN_TO_DASHBOARD,
    BRIDGE_NODE_CREATED,
    hueBridgeStatuses,
    PAIR_WITH_BRIDGE,
    POLL_HUE_BRIDGE_STATUS,
    SEARCH_FOR_BRIDGE,
    transientStatuses
} from './constants';
import { bridgeNodeCreated, pollBridgeStatus, bridgeStatusUpdated, addChildrenToDashboard } from './actions';
import type {
    BridgeIdPayload,
    BridgeNodeCreatedPayload,
    BridgeStatusPayload,
    OptionalBridgeStatusPayload,
    ParentPayload
} from './types';

import { dashboardAddProduct, saveDashboardEdit } from '~/core/state/dashboard/actions';

import { getProducts } from '~/core/state/products/actions';
import { loadDevices } from '~/core/state/devices/actions';
import { getDevicesByParentId } from '~/core/state/devices/selectors';
import { RECEIVE_PRODUCTS } from '~/core/state/products/constants';
import { RECEIVE_DEVICES } from '~/core/state/devices/constants';

export function* pollBridgeStatusSaga({
    payload: { bridgeId, currentStatus }
}: OptionalBridgeStatusPayload): Generator<noop | Effect, *, *> {
    if (bridgeId) {
        const [response, error] = yield call(handleApi(getDevice), bridgeId);

        if (error) {
            console.error('This is where error handling will be - part of another story');
            console.error(error);
        }

        const { props: { status }, id } = response;
        if (status !== currentStatus) {
            yield put(bridgeStatusUpdated(response));
        }

        if (status === hueBridgeStatuses.AUTHENTICATED) {
            yield put(addChildrenToDashboard(id));
        }

        if (transientStatuses.includes(status)) {
            yield call(delay, 2000);
            yield put(pollBridgeStatus(bridgeId, status));
        }
    }
}

export function* addChildrenToDashboardSaga({ payload: { parent } }: ParentPayload): Generator<noop | Effect, *, *> {
    yield put(getProducts());
    yield put(loadDevices());

    yield all([take(RECEIVE_PRODUCTS), take(RECEIVE_DEVICES)]);

    // Add new products to dashboard cutomisation
    // $FlowFixMe
    const hueDevices = yield select(getDevicesByParentId, { parent });

    for (const device of hueDevices) {
        yield put(dashboardAddProduct(device.id));
    }

    yield put(saveDashboardEdit());
}

export function* createBridgeSD(): Generator<Effect | noop, *, *> {
    const [{ poll, id }, error] = yield call(handleApi(api.createLinkToBridge));

    if (error) {
        console.error('This is where error handling will be - part of another story');
        console.error(error);
    }

    if (id) {
        yield put(bridgeNodeCreated({ nodeId: id }));
    } else {
        yield put(
            pollNodeExists({
                id: poll,
                nextAction: bridgeNodeCreated
            })
        );
    }
}

export function* searchForBridgeAgain(bridgeId: string): Generator<Effect | noop, *, *> {
    const [, error] = yield call(handleApi(api.searchForBridgeAgain), bridgeId);

    if (error) {
        console.error('This is where error handling will be - part of another story');
        console.error(error);
    }

    yield put(pollBridgeStatus(bridgeId, hueBridgeStatuses.SEARCHING));
}

export function* searchForBridgeSaga({ payload: { bridgeId } }: BridgeIdPayload): Generator<Effect | noop, *, *> {
    if (!bridgeId) {
        yield call(createBridgeSD);
    } else {
        yield call(searchForBridgeAgain, bridgeId);
    }
}

export function* pairWithBridgeSaga({
    payload: { bridgeId, currentStatus }
}: BridgeStatusPayload): Generator<Effect | noop, *, *> {
    const [, error] = yield call(handleApi(api.pairWithBridge), bridgeId);

    if (error) {
        console.error('This is where error handling will be - part of another story');
        console.error(error);
    }

    yield put(pollBridgeStatus(bridgeId, currentStatus));
}

export function* bridgeNodeCreatedSaga({
    payload: { nodeId } = {},
    error
}: BridgeNodeCreatedPayload): Generator<Effect | noop, *, *> {
    if (!error) {
        yield put(navigate(formatPattern(installPhilipsHue, { id: nodeId })));
    }
}

function* watchHueBridgeNodeCreated(): Generator<Effect | noop, *, *> {
    yield takeEvery(BRIDGE_NODE_CREATED, handleSaga(bridgeNodeCreatedSaga));
}

function* watchSearchForBridge(): Generator<Effect | noop, *, *> {
    yield takeEvery(SEARCH_FOR_BRIDGE, handleSaga(searchForBridgeSaga));
}

function* watchPairWithBridge(): Generator<Effect | noop, *, *> {
    yield takeEvery(PAIR_WITH_BRIDGE, handleSaga(pairWithBridgeSaga));
}

function* watchPollBridgeStatus(): Generator<Effect | noop, *, *> {
    yield takeEvery(POLL_HUE_BRIDGE_STATUS, handleSaga(pollBridgeStatusSaga));
}

function* watchAddChildrenToDashboard(): Generator<Effect | noop, *, *> {
    yield takeEvery(ADD_CHILDREN_TO_DASHBOARD, handleSaga(addChildrenToDashboardSaga));
}

export default [
    watchSearchForBridge,
    watchHueBridgeNodeCreated,
    watchPollBridgeStatus,
    watchPairWithBridge,
    watchAddChildrenToDashboard
];



// WEBPACK FOOTER //
// ./src/core/state/install-hue-bridge/sagas.js